import { createFileRoute, redirect } from '@tanstack/react-router'
import { databases } from '@/lib/appwrite'
import { Query } from 'appwrite'
import type { Instrument } from '@/Wist-blockchain/collections/instruments/types'
import type { Company } from '@/lib/collections/organizations/getOrganizationsFetch'
import type { ServiceReport } from '@/Wist-blockchain/collections/serviceReport/types'
import type { RevisionMode } from '@/Wist-blockchain/serviceReport/utils/useServiceReportForm'
import AddServiceReport from '@/Wist-blockchain/serviceReport/view/user/addServiceReport'

const DB = import.meta.env.VITE_BLOCKCHAIN_DATABASE_ID
const COL_REPORT = import.meta.env.VITE_BLOCKCHAIN_SERVICE_REPORT_COLLECTION_ID
const COL_ACTIVITIES = import.meta.env.VITE_BLOCKCHAIN_REPORT_ACTIVITIES_COLLECTION_ID
const COL_MATERIALS = import.meta.env.VITE_BLOCKCHAIN_REPORT_MATERIALS_COLLECTION_ID

export const Route = createFileRoute('/_authenticated/instruments/serviceReport/$id/$draftId')({
    beforeLoad: ({ context }) => {
        if (!context.user) throw redirect({ to: '/login' })
        const { isServicioTecnico, isAdmin } = context.userRoles
        if (!isServicioTecnico && !isAdmin) throw redirect({ to: '/unauthorized' })
    },
    loader: async ({ params }) => {
        const { id, draftId } = params

        const instrument = await databases.getDocument(
            DB,
            import.meta.env.VITE_BLOCKCHAIN_INSTRUMENTS_COLLECTION_ID,
            id
        ) as unknown as Instrument

        const company = instrument.idOrganization
            ? await databases.getDocument(
                import.meta.env.VITE_APPWRITE_DATABASE_ID,
                import.meta.env.VITE_ORGANIZATIONS_COLLECTION_ID,
                instrument.idOrganization
            ) as unknown as Company
            : undefined

        const draft = await databases.getDocument(DB, COL_REPORT, draftId) as unknown as ServiceReport

        const [acts, mats] = await Promise.all([
            databases.listDocuments(DB, COL_ACTIVITIES, [
                Query.equal("idReport", draft.idReport),
                Query.orderAsc("order"),
            ]),
            databases.listDocuments(DB, COL_MATERIALS, [
                Query.equal("idReport", draft.idReport),
                Query.orderAsc("order"),
            ]),
        ])

        // Detectar si el borrador es una revisión (parentReportId guardado)
        let revisionMode: RevisionMode | undefined
        if (draft.parentReportId) {
            const existingRevisions = await databases.listDocuments(DB, COL_REPORT, [
                Query.equal("parentReportId", draft.parentReportId),
                Query.limit(1),
            ])
            revisionMode = {
                originalIdReport: draft.parentReportId,
                nextRevisionNumber: draft.revisionNumber !== null ? Number(draft.revisionNumber) : existingRevisions.total,
            }
        }

        return {
            instrument,
            company,
            draftId,
            initialDraft: draft,
            initialActivities: acts.documents,
            initialMaterials: mats.documents,
            revisionMode,
        }
    },
    errorComponent: () => <div>Borrador no encontrado.</div>,
    component: RouteComponent,
})

function RouteComponent() {
    const {
        instrument,
        company,
        draftId,
        initialDraft,
        initialActivities,
        initialMaterials,
        revisionMode,
    } = Route.useLoaderData()

    return (
        <AddServiceReport
            instrument={instrument}
            company={company}
            draftId={draftId}
            initialDraft={initialDraft}
            initialActivities={initialActivities}
            initialMaterials={initialMaterials}
            revisionMode={revisionMode}
        />
    )
}
